import React, { useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import './DesignTaskCreateWizard.css';

const REASON_OPTIONS = [
  { value: 'new-design',         label: 'New design' },
  { value: 'improvement',        label: 'Improvement' },
  { value: 'structural-issue',   label: 'Structural issue' },
  { value: 'prototype-feedback', label: 'Prototype build feedback' },
  { value: 'compliance-gap',     label: 'Compliance gap' },
  { value: 'test-result',        label: 'Test result' },
  { value: 'supplier-input',     label: 'Supplier input' },
  { value: 'corrective-action',  label: 'Corrective action' },
];

const URGENCY_OPTIONS = [
  { value: 'routine',         label: 'Routine' },
  { value: 'expedited',       label: 'Expedited' },
  { value: 'safety-critical', label: 'Safety-critical' },
];

const SCOPE_OPTIONS = [
  {
    value: 'initial-creation',
    label: 'Initial creation',
    description: 'Producing new design data for the first time. No prior released version exists.',
  },
  {
    value: 'change-to-released',
    label: 'Change to released data',
    description: 'Modifying released design data or a frozen baseline.',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'Single Design Task that creates new data AND modifies released data within one scope.',
  },
];

function DesignTaskCreateWizard({ projectId, onClose, onCreated }) {
  const [form, setForm] = useState({
    scopeType: 'initial-creation',
    title: '',
    description: '',
    reason: 'new-design',
    urgency: 'routine',
    proposedAction: '',
    proposedClassification: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const needsClassification = ['change-to-released', 'hybrid'].includes(form.scopeType);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (needsClassification && !form.proposedClassification) {
      setError('Proposed classification is required for Change and Hybrid scope.');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        scopeType: form.scopeType,
        title: form.title.trim(),
        description: form.description,
        reason: form.reason,
        urgency: form.urgency,
        proposedAction: form.proposedAction,
        ...(needsClassification ? { proposedClassification: form.proposedClassification } : {}),
      };
      const res = await axios.post(`/projects/${projectId}/designtasks`, body);
      onCreated(res.data.designTask);
    } catch (err) {
      setError(err.response?.data?.error
            || err.response?.data?.errors?.[0]?.msg
            || 'Failed to create Design Task.');
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">State 1 · Initiated</p>
            <h2>New Design Task</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">

          {/* SCOPE TYPE — three-card picker */}
          <fieldset className="dt-fieldset">
            <legend>Scope type *</legend>
            <div className="dt-scope-grid">
              {SCOPE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`dt-scope-card ${form.scopeType === opt.value ? 'is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="scopeType"
                    value={opt.value}
                    checked={form.scopeType === opt.value}
                    onChange={() => set('scopeType', opt.value)}
                  />
                  <span className="dt-scope-card-label">{opt.label}</span>
                  <span className="dt-scope-card-desc">{opt.description}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* TITLE */}
          <div className="dt-field">
            <label htmlFor="dt-title">Title *</label>
            <input
              id="dt-title"
              type="text"
              maxLength={120}
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder='e.g. "Create vertical tail structure for Strigoi C1"'
              required
              autoFocus
            />
            <span className="dt-field-hint">{form.title.length}/120</span>
          </div>

          {/* DESCRIPTION */}
          <div className="dt-field">
            <label htmlFor="dt-description">Description</label>
            <textarea
              id="dt-description"
              rows={4}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="What the task creates or changes, and why"
            />
          </div>

          {/* REASON + URGENCY — side by side */}
          <div className="dt-field-row">
            <div className="dt-field">
              <label htmlFor="dt-reason">Reason *</label>
              <select id="dt-reason" value={form.reason} onChange={e => set('reason', e.target.value)}>
                {REASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="dt-field">
              <label htmlFor="dt-urgency">Urgency *</label>
              <select id="dt-urgency" value={form.urgency} onChange={e => set('urgency', e.target.value)}>
                {URGENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* CLASSIFICATION — only for Change / Hybrid */}
          {needsClassification && (
            <div className="dt-field">
              <label htmlFor="dt-class">Proposed classification *</label>
              <div className="dt-class-row">
                {['class-1', 'class-2', 'class-3'].map(c => (
                  <label
                    key={c}
                    className={`dt-class-chip ${form.proposedClassification === c ? 'is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="proposedClassification"
                      value={c}
                      checked={form.proposedClassification === c}
                      onChange={() => set('proposedClassification', c)}
                    />
                    {c.replace('-', ' ').toUpperCase()}
                  </label>
                ))}
              </div>
              <span className="dt-field-hint">
                Cristian will validate this in State 2; you can be overruled.
              </span>
            </div>
          )}

          {/* PROPOSED ACTION */}
          <div className="dt-field">
            <label htmlFor="dt-action">Proposed action</label>
            <textarea
              id="dt-action"
              rows={3}
              value={form.proposedAction}
              onChange={e => set('proposedAction', e.target.value)}
              placeholder="What you propose to do (one paragraph is fine)"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Raise Design Task'}
            </button>
          </div>

          <p className="dt-wizard-foot">
            On submit, the task is logged at State 1 and auto-advances to State 2 (Classified)
            with a classifier assignment for the Config &amp; Compliance lead.
          </p>
        </form>
      </div>
    </div>
  );
}

export default DesignTaskCreateWizard;
